import { lessonsFor, mc, tf } from "./helpers";

const studio = { label: "Prompt Studio", href: "/prompt-studio" };
const training = { label: "Prompt Training", href: "/prompt-training" };
const factory = { label: "Prompt Template Factory", href: "/prompt-template-factory" };

export const PROMPTING_LESSONS = lessonsFor("prompting", [
  {
    slug: "start-with-outcome",
    title: "Start With the Outcome",
    minutes: 4,
    learn: ["Lead with what you want to end up with", "Turn vague asks into clear goals"],
    explanation: [
      "AI does better when it knows the finish line. Start by stating the result you want, not just the topic.",
      "\"Help with my email\" becomes \"Write a short email asking my manager to move our meeting to Thursday.\"",
    ],
    example: "Before: \"Write me an email.\" After: \"Write an email that gets a client to confirm a delivery date by Friday.\"",
    interactive: "prompt-makeover",
    check: mc("single", "Which prompt states a clear outcome?", ["Marketing ideas", "*Give me five subject lines for a spring sale email to existing customers", "Help"], "It names the deliverable, the purpose and the audience."),
    takeaway: "Say what \"done\" looks like.",
    tool: studio,
    resource: training,
  },
  {
    slug: "useful-context",
    title: "Give Useful Context",
    minutes: 4,
    learn: ["What context AI needs", "Leaving out what doesn't help"],
    explanation: [
      "Context is the background a smart helper would need: the situation, what you've tried and any key facts.",
      "Keep it relevant. A few clear sentences beat a wall of pasted text.",
    ],
    example: "\"I run a small bakery. We're closing early on Saturday for a family event. Draft a friendly sign for the door.\"",
    check: tf("More context is always better, no matter how much.", false, "Relevant context helps; unrelated text can distract the AI."),
    takeaway: "Give the background a helpful person would ask for.",
    tool: studio,
    resource: training,
  },
  {
    slug: "define-audience",
    title: "Define the Audience",
    minutes: 3,
    learn: ["Why audience changes the answer", "How to describe an audience quickly"],
    explanation: [
      "The same message reads differently for a CEO, a customer or a 10-year-old. Tell AI who will read the result.",
    ],
    example: "\"Explain two-factor authentication for retirees who are new to smartphones.\"",
    check: mc("single", "Which detail most improves an explanation prompt?", ["*Who the explanation is for", "The color of your screen", "How long you've used AI"], "Audience shapes vocabulary, length and examples."),
    takeaway: "Name the reader.",
    tool: studio,
    resource: training,
  },
  {
    slug: "add-constraints",
    title: "Add Constraints",
    minutes: 4,
    learn: ["Length, tone and must-include rules", "Saying what to avoid"],
    explanation: [
      "Constraints are the guardrails: word count, tone, things to include and things to avoid.",
      "Clear limits save rounds of editing.",
    ],
    example: "\"Under 120 words, warm but professional, mention the refund policy, no exclamation marks.\"",
    check: mc("multi", "Which are useful constraints? (Choose all that apply)", ["*Keep it under 100 words", "*Use a calm, reassuring tone", "Be good", "*Don't mention pricing"], "Specific, checkable limits work; vague ones like \"be good\" don't."),
    takeaway: "Set limits the AI can actually follow.",
    tool: studio,
    resource: training,
  },
  {
    slug: "output-format",
    title: "Ask for the Output Format",
    minutes: 3,
    learn: ["Requesting tables, bullets or steps", "Making results easy to reuse"],
    explanation: ["Tell AI the shape you need: a bulleted list, a table with named columns, numbered steps or a short paragraph."],
    example: "\"Return a table with columns: Task, Owner, Due date.\"",
    check: tf("You can ask AI to answer as a table.", true, "Asking for a format makes results easier to scan and reuse."),
    takeaway: "Ask for the format you'll actually use.",
    tool: factory,
    resource: training,
  },
  {
    slug: "iteration",
    title: "Improve Through Iteration",
    minutes: 4,
    learn: ["Refining instead of restarting", "Giving specific feedback"],
    explanation: [
      "The first answer is a draft. Tell AI what to change: \"shorter\", \"more formal\", \"add an example about pricing\".",
      "Specific feedback beats \"try again\".",
    ],
    example: "\"Good start. Cut it to three sentences and make the opening friendlier.\"",
    check: mc("single", "What's the most useful follow-up?", ["Try again", "*Make it shorter and add one example for small businesses", "That's wrong"], "Specific changes give the AI something concrete to act on."),
    takeaway: "Treat answers as drafts and steer them.",
    tool: { label: "Chat with GenieBot", href: "/chat" },
    resource: training,
  },
  {
    slug: "few-shot-examples",
    title: "Examples and Few-Shot Prompting",
    minutes: 5,
    difficulty: "Intermediate",
    learn: ["Showing AI an example of what you want", "When examples help most"],
    explanation: [
      "Few-shot prompting means including one or more examples of the result you want. AI copies the pattern — style, length and structure.",
      "It's especially helpful for consistent formats like product descriptions or social posts.",
    ],
    example: "\"Here are two product descriptions I like: … Write one in the same style for our new water bottle.\"",
    check: tf("Giving an example can help AI match your style.", true, "Examples show the pattern more clearly than describing it."),
    takeaway: "Show, don't just tell.",
    tool: factory,
    resource: training,
  },
  {
    slug: "fresh-context",
    title: "When to Start a Fresh Context",
    minutes: 3,
    learn: ["Signs a conversation has drifted", "Restarting with a clean summary"],
    explanation: [
      "Long chats can drift as earlier instructions get mixed with new ones. If answers keep missing the point, start a new chat.",
      "Paste a short summary of what matters so you don't lose progress.",
    ],
    example: "After 40 messages about a trip plan, start fresh with: \"Plan a 3-day Chicago trip for two, mid-range budget, museums and food.\"",
    check: mc("scenario", "The AI keeps mixing up two projects in one long chat. What should you do?", ["Keep correcting it forever", "*Start a new chat with a short summary of the one project", "Switch to all capital letters"], "A fresh context with a clear summary resets the confusion."),
    takeaway: "When it drifts, restart clean with a summary.",
    tool: { label: "Chat with GenieBot", href: "/chat" },
    resource: training,
  },
]);
